import { useTranslation } from 'react-i18next';

import { useQuery } from '@tanstack/react-query';
import { validate as validateUUID } from 'uuid';

import { normalizeAdditionalRequest } from '@spacefill/shared/src/constants/AdditionalRequests';
import { formatAddress } from '@spacefill/shared/src/utils/Address';
import { call } from '@spacefill/shared/src/utils/Backend';
import { getCountryNameFromCode } from '@spacefill/shared/src/utils/CountryUtils';
import { useFeatureTogglingContext } from '@spacefill/shared/src/utils/FeatureToggling/FeatureToggling';
import { AvailableToggleFeatures } from '@spacefill/shared/src/utils/FeatureToggling/availableToggleFeatures';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';

import {
    BreadcrumCarrierLabel,
    BreadcrumbAppointmentLabel,
    BreadcrumbAutomaticDataCompletionRuleLabel,
    BreadcrumbOrderLabel,
    BreadcrumbOrderTabLabel,
    BreadcrumbTabLabel,
    BreadcrumbTourLabel,
    BreadcrumbTransportLabel,
    BreadcrumbViewLabel,
} from './BreadcrumbHelper';

/* eslint-disable react-hooks/rules-of-hooks */
export const routes = [
    {
        path: '/',
        breadcrumb: () => {
            const { t } = useTranslation();
            const { features } = useFeatureTogglingContext();
            const { user } = useUserContext();

            if (!features?.[AvailableToggleFeatures.welcome_page]) {
                return user?.customer?.company || user?.logisticProvider?.company || user?.carrier?.company;
            }
            return t('Home');
        },
    },
    {
        path: '/warehouses/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Warehouses');
        },
    },
    {
        path: '/warehouses/:warehouseId',
        breadcrumb: ({ match }) => {
            const { user } = useUserContext();
            const warehouse = user?.customerWarehouseConfigurations?.find(
                (cwc) => cwc.warehouseId === match.params.warehouseId
            )?.warehouse;

            if (!warehouse) {
                return match.params.warehouseId;
            }

            return warehouse.label;
        },
    },
    {
        path: '/warehouses/:warehouseId/edit',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Edit');
        },
    },
    {
        path: '/automations/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Automations');
        },
    },
    {
        path: '/automations/automatic-data-completion/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Automatic data completion');
        },
    },
    {
        path: '/automations/automatic-data-completion/new/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New rule');
        },
    },
    {
        path: '/automations/automatic-data-completion/:ruleId',
        breadcrumb: BreadcrumbAutomaticDataCompletionRuleLabel,
    },
    {
        path: '/automations/automatic-data-completion/:ruleId/edit/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Edit the rule');
        },
    },
    {
        path: '/automations/order-routing/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Order Routing');
        },
    },
    {
        path: '/automations/order-sync-conditions/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Order synchronization conditions');
        },
    },
    {
        path: '/automations/incident-auto-create/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Automated incident creation');
        },
    },
    {
        path: '/automations/incident-auto-create/new-rule',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New rule');
        },
    },
    {
        path: '/automations/incident-auto-create/:ruleId',
        breadcrumb: ({ match }) => {
            const { data, error, isLoading } = useQuery({
                queryKey: [`/triggered-rules/${match.params.ruleId}`],
                queryFn: () => call(`/triggered-rules/${match.params.ruleId}`),
                enabled: validateUUID(match.params.ruleId),
            });

            if (isLoading || error) {
                return '';
            }
            return data?.name;
        },
    },
    {
        path: '/automations/incident-auto-create/:id/edit',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Edit rule');
        },
    },
    {
        path: '/automations/incident-auto-create/runs',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Execution history');
        },
    },
    {
        path: '/company',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('breadcrumb.Company');
        },
    },
    {
        path: '/company/profile',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Company profile');
        },
    },
    {
        path: '/company/coworkers',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Coworkers');
        },
    },
    {
        path: '/profile/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('User profile');
        },
    },
    {
        path: '/inventory/',
        breadcrumb: null,
    },
    {
        path: '/inventory/items/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('References');
        },
    },
    {
        path: '/inventory/items/new',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New');
        },
    },
    {
        path: '/inventory/items/:masterItemId/edit',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Edit');
        },
    },
    {
        path: '/inventory/items/:masterItemId/',
        breadcrumb: ({ match }) => {
            if (['archived', 'unarchived'].includes(match.params.masterItemId)) {
                return '';
            }
            const { data, error, isLoading } = useQuery({
                queryKey: ['master-items', match.params.masterItemId],
                queryFn: () => call(`master-items/${match.params.masterItemId}`),
            });

            if (isLoading || error || !data) {
                return '';
            }

            return data?.itemReference;
        },
    },
    {
        path: '/inventory/items/:masterItemId/:tab/',
        breadcrumb: BreadcrumbTabLabel,
    },
    {
        path: '/stock-situation/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Stock situation');
        },
    },
    {
        path: '/stock-situation/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/inventory/stock-situation/references',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('By reference');
        },
    },
    {
        path: '/inventory/stock-situation/references/forecasted',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Forecasted');
        },
    },
    {
        path: '/inventory/stock-situation/references/accurate',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Accurate stock situation');
        },
    },
    {
        path: '/inventory/stock-situation/unique-identifier',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('By unique identifier');
        },
    },
    {
        path: '/orders/:orderId/order-adjustment/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Order adjustment');
        },
    },
    {
        path: '/inventory/:warehouseId/stock-adjustment/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Stock adjustment');
        },
    },
    {
        path: '/warehouses/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Warehouses');
        },
    },
    {
        path: '/warehouses/:warehouseId/',
        breadcrumb: ({ match }) => {
            const { user } = useUserContext();
            const warehouse = user?.customerWarehouseConfigurations?.find(
                (cwc) => cwc.warehouseId === match.params.warehouseId
            )?.warehouse;

            if (!warehouse) {
                return match.params.warehouseId;
            }

            return warehouse.label;
        },
    },
    {
        path: '/exit-orders/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Exit_plural');
        },
    },
    {
        path: '/entry-orders',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Entry_plural');
        },
    },
    {
        path: '/exit-orders/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/entry-orders/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/orders/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Orders');
        },
    },
    {
        path: '/exit-orders/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Exit_plural');
        },
    },
    {
        path: '/exit-orders/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Exit_plural');
        },
    },
    {
        path: '/entry-orders/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Entry_plural');
        },
    },
    {
        path: '/entry-orders/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Entry_plural');
        },
    },
    {
        path: '/return-orders/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Return_plural');
        },
    },
    {
        path: '/return-orders/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Return_plural');
        },
    },
    {
        path: '/return-orders/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/entry-orders/init',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New entry');
        },
    },
    {
        path: '/exit-orders/init',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New exit');
        },
    },
    {
        path: '/return-orders/init',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New return');
        },
    },
    {
        path: '/sales/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Sales');
        },
    },
    {
        path: '/sales/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/sales/new',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New sale');
        },
    },
    // Transports
    {
        path: '/transports/init',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New transport order');
        },
    },
    {
        path: '/transports/new',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New transport order');
        },
    },
    {
        path: '/transports/:orderId',
        breadcrumb: BreadcrumbTransportLabel,
    },
    // Tours
    {
        path: '/tours/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Tours');
        },
    },
    {
        path: '/tours/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/tours/:tourId',
        breadcrumb: BreadcrumbTourLabel,
    },
    {
        path: '/tours/:tourId/:tab/',
        breadcrumb: BreadcrumbTabLabel,
    },
    // Unconfirmed transports
    {
        path: '/unconfirmed-transports',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Transports to review');
        },
    },
    {
        path: '/unconfirmed-transports/:transportId/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Create transport order');
        },
    },
    {
        path: '/entry-orders/new',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New entry');
        },
    },
    {
        path: '/exit-orders/new',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New exit');
        },
    },
    {
        path: '/return-orders/new',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('New return');
        },
    },
    {
        path: '/entry-orders/:orderId/',
        breadcrumb: BreadcrumbOrderLabel,
    },
    {
        path: '/exit-orders/:orderId/',
        breadcrumb: BreadcrumbOrderLabel,
    },
    {
        path: '/return-orders/:orderId/',
        breadcrumb: BreadcrumbOrderLabel,
    },
    {
        path: '/entry-orders/:orderId/:tab/',
        breadcrumb: BreadcrumbOrderTabLabel,
    },
    {
        path: '/exit-orders/:orderId/:tab/',
        breadcrumb: BreadcrumbOrderTabLabel,
    },
    {
        path: '/return-orders/:orderId/:tab/',
        breadcrumb: BreadcrumbOrderTabLabel,
    },
    {
        path: '/entry-orders/:orderId/edit',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Edit');
        },
    },
    {
        path: '/exit-orders/:orderId/edit',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Edit');
        },
    },

    {
        path: '/unconfirmed-orders',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Orders to review');
        },
    },
    {
        path: '/unconfirmed-entries',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Entries to review');
        },
    },
    {
        path: '/unconfirmed-exits',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Exits to review');
        },
    },
    {
        path: '/unconfirmed-entries/:emailId/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Create entry');
        },
    },
    {
        path: '/unconfirmed-exits/:emailId/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Create exit');
        },
    },
    {
        path: '/unconfirmed-orders/:emailId/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Create Order');
        },
    },
    {
        path: '/inventory/stock-situation/new-adjustment',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Stock adjustment');
        },
    },
    {
        path: '/incidents/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Incidents');
        },
    },
    {
        path: '/incidents/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/documents/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Documents');
        },
    },
    {
        path: '/documents/:tab/',
        breadcrumb: BreadcrumbTabLabel,
    },
    {
        path: '/settings/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Settings');
        },
    },
    {
        path: '/settings/connectors',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Connectors');
        },
    },
    {
        path: '/settings/storages',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Warehouses');
        },
    },
    {
        path: '/settings/warehouses',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Warehouses');
        },
    },
    {
        path: '/settings/address-book',
        breadcrumb() {
            const { t } = useTranslation();
            return t('Address book');
        },
    },
    {
        path: '/settings/address-book/new',
        breadcrumb() {
            const { t } = useTranslation();
            return t('New');
        },
    },
    {
        path: '/settings/carriers',
        breadcrumb() {
            const { t } = useTranslation();
            return t('Carriers');
        },
    },
    {
        path: '/settings/incidents',
        breadcrumb() {
            const { t } = useTranslation();
            return t('Incidents');
        },
    },
    {
        path: '/settings/document-types',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Document types');
        },
    },
    {
        path: '/settings/additional-request-types',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Additional request types');
        },
    },
    {
        path: '/settings/display',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Display');
        },
    },
    {
        path: '/settings/shippers/:customerId/carriers/:carrierId',
        breadcrumb: BreadcrumCarrierLabel,
    },
    {
        path: '/settings/carriers/:carrierId',
        breadcrumb: BreadcrumCarrierLabel,
    },
    {
        path: '/settings/address-book/:addressId',
        breadcrumb({ match }) {
            const { i18n } = useTranslation();

            const { data, error, isLoading } = useQuery({
                queryKey: ['addresses/verified', match.params.addressId],
                queryFn: () => call(`addresses/verified/${match.params.addressId}`),
            });

            if (isLoading || error) {
                return match.params.addressId;
            }
            return formatAddress({
                addressLine1: data?.addressLine1,
                zipCode: data?.zipCode,
                city: data?.city,
                country: getCountryNameFromCode(data?.countryCode, i18n.language, data?.country),
            });
        },
    },
    {
        path: '/settings/address-book/:addressId/edit',
        breadcrumb() {
            const { t } = useTranslation();
            return t('Edit');
        },
    },
    {
        path: '/settings/appointments',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Appointments');
        },
    },
    {
        path: '/settings/shippers/:customerId',
        breadcrumb: ({ match }) => {
            const { user } = useUserContext();
            const customer = user?.customerWarehouseConfigurations?.find(
                (cwc) => cwc.customerId === match.params.customerId
            )?.customer;

            if (!customer) {
                return match.params.customerId;
            }

            return customer.company;
        },
    },
    {
        path: '/settings/shippers/:customerId/users',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Coworkers');
        },
    },
    {
        path: '/settings/shippers/:customerId/custom-fields',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Custom fields');
        },
    },
    {
        path: '/settings/shippers/:customerId/carriers',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Carriers');
        },
    },
    {
        path: '/settings/shippers/:customerId/incidents',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Incidents');
        },
    },
    {
        path: '/settings/shippers/:customerId/document-types',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Document types');
        },
    },
    {
        path: '/settings/shippers/:customerId/additional-request-types',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Additional request types');
        },
    },
    {
        path: '/settings/shippers/:customerId/display',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Display');
        },
    },
    {
        path: '/settings/shippers/:customerId/carriers/:carrierId',
        breadcrumb: BreadcrumCarrierLabel,
    },
    {
        path: '/appointments/list/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/appointments/list/:appointmentId',
        breadcrumb: BreadcrumbAppointmentLabel,
    },
    {
        path: '/appointments/list/view/:viewId/:appointmentId',
        breadcrumb: BreadcrumbAppointmentLabel,
    },
    {
        path: '/settings/custom-fields/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Custom fields');
        },
    },
    {
        path: '/warehouses/',
        breadcrumb: null,
    },
    {
        path: '/warehouses/:warehouseId',
        breadcrumb: null,
    },
    {
        path: '/appointments/',
        breadcrumb: null,
    },
    {
        path: '/appointments/list/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Planning');
        },
    },
    {
        path: '/appointments/calendar/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Planning');
        },
    },

    {
        path: '/integrations/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Connectivity');
        },
    },
    {
        path: '/integrations/jobs/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Jobs');
        },
    },
    {
        path: '/integrations/api-calls/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('API calls');
        },
    },
    {
        path: '/integrations/jobs/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/integrations/api-calls/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/analytics/',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Dashboards');
        },
    },
    {
        path: '/additional-requests/',
        breadcrumb() {
            const { t } = useTranslation();
            return t('Additional requests');
        },
    },
    {
        path: '/additional-requests/:additionalRequestId',
        breadcrumb({ match }) {
            const { data, error, isLoading } = useQuery({
                queryKey: [`/additional-requests/${match.params.additionalRequestId}/view`],
                queryFn: async () => {
                    const response = await call(`/additional-requests/${match.params.additionalRequestId}/view`);
                    return normalizeAdditionalRequest(response);
                },
                enabled: validateUUID(match.params.additionalRequestId),
            });

            if (isLoading || error || !data) {
                return <>{match.params.additionalRequestId}</>;
            }

            return data?.iid;
        },
    },
    {
        path: '/additional-requests/view/:viewId',
        breadcrumb: BreadcrumbViewLabel,
    },
    {
        path: '/sales/:saleId/',
        breadcrumb: BreadcrumbOrderLabel,
    },
    {
        path: '/sales/:saleId/edit',
        breadcrumb: () => {
            const { t } = useTranslation();
            return t('Edit');
        },
    },
];
/* eslint-enable react-hooks/rules-of-hooks */
